import { isNativeApp } from './platform';
import { PLAY_GA4_MEASUREMENT_ID as PLAY_MEASUREMENT_ID } from '@/types/websiteAnalytics';
export { PLAY_GA4_MEASUREMENT_ID as PLAY_MEASUREMENT_ID } from '@/types/websiteAnalytics';

export const PLAY_CONSENT_KEY = 'grundo.play.analytics-consent.v1';
/** Settings → Cookie settings reopens the consent panel (no floating button, Geri, #103). */
export const PLAY_CONSENT_OPEN_EVENT = 'grundo:play-consent-open';
export function openPlayConsentSettings() { window.dispatchEvent(new Event(PLAY_CONSENT_OPEN_EVENT)); }
const MAX_CONSENT_AGE = 180 * 86_400_000;
interface Consent { version: 1; analytics: boolean; savedAt: number }
type GoogleWindow = Window & Partial<Record<`ga-disable-${typeof PLAY_MEASUREMENT_ID}`, boolean>> & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
const DISABLE_KEY = `ga-disable-${PLAY_MEASUREMENT_ID}` as const;
let configured = false;
let lastPath: string | null = null;
let previousLocation = '';

export function isPlayAnalyticsSurface(hostname = typeof window === 'undefined' ? '' : window.location.hostname, native = isNativeApp()): boolean {
  return hostname === 'play.grundoapp.com' && !native;
}
/** OBS overlay (`?mod=pip`): no consent panel and no measurement — it would show on the stream (Geri, 10-24). */
export function isPlayOverlay(search: string): boolean {
  return new URLSearchParams(search).get('mod') === 'pip';
}
export function readPlayConsent(storage?: Pick<Storage, 'getItem'>, now = Date.now()): Consent | null {
  try {
    const value: unknown = JSON.parse((storage ?? localStorage).getItem(PLAY_CONSENT_KEY) ?? 'null');
    if (value && typeof value === 'object' && 'version' in value && value.version === 1 && 'analytics' in value && typeof value.analytics === 'boolean' && 'savedAt' in value && typeof value.savedAt === 'number' && value.savedAt <= now && value.savedAt > now - MAX_CONSENT_AGE) return value as Consent;
  } catch { /* Optional measurement stays off if storage is unavailable. */ }
  return null;
}

/** Only route categories leave the app; IDs, usernames, query strings and titles never do. */
export function playAnalyticsPath(path: string): string | null {
  const first = path.split(/[/?#]/)[1] ?? '';
  if (first === 'admin') return null;
  if (!first) return '/';
  const categories = ['belepes', 'regisztracio', 'elfelejtett-jelszo', 'beallitasok', 'profil', 'aktivitas', 'bandak', 'banda', 'kozosseg', 'terkep', 'terulet', 'rogzites', 'felfedezes', 'kereses', 'kuldetesek', 'ertesitesek', 'uzenetek', 'statisztika', 'ranglista', 'elo', 's', 'auth'];
  return categories.includes(first) ? `/${first}` : '/egyeb';
}
function target(): GoogleWindow { return window as GoogleWindow; }
function disable() {
  target()[DISABLE_KEY] = true;
  if (configured) target().gtag?.('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  lastPath = null; previousLocation = '';
}
function clearCookies() {
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.trim().split('=')[0]!;
    if (!name.startsWith('grundo_play')) continue;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=play.grundoapp.com; SameSite=Lax`;
  }
}
export function savePlayConsent(analytics: boolean): Consent {
  const consent: Consent = { version: 1, analytics, savedAt: Date.now() };
  try { localStorage.setItem(PLAY_CONSENT_KEY, JSON.stringify(consent)); } catch { /* Do not block the app. */ }
  if (!analytics) { disable(); clearCookies(); }
  return consent;
}
export function trackPlayPage(pathname: string) {
  if (!isPlayAnalyticsSurface()) return;
  const path = playAnalyticsPath(pathname);
  if (!readPlayConsent()?.analytics || path === null) { disable(); return; }
  const google = target(), location = `https://play.grundoapp.com${path}`;
  google[DISABLE_KEY] = false;
  google.dataLayer ??= [];
  google.gtag ??= function () { google.dataLayer!.push(arguments); };
  const page = { page_location: location, page_referrer: previousLocation || safeReferrer(), page_title: 'GRUNDO webapp' };
  if (!configured) {
    configured = true;
    google.gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    google.gtag('set', 'ads_data_redaction', true);
    google.gtag('js', new Date());
    google.gtag('config', PLAY_MEASUREMENT_ID, { ...page, send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false, cookie_domain: 'play.grundoapp.com', cookie_prefix: 'grundo_play' });
  }
  google.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  if (!document.getElementById('grundo-play-ga4')) {
    const script = document.createElement('script'); script.id = 'grundo-play-ga4'; script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${PLAY_MEASUREMENT_ID}`;
    script.onerror = () => script.remove(); document.head.appendChild(script);
  }
  if (lastPath === pathname) return;
  google.gtag('config', PLAY_MEASUREMENT_ID, { ...page, send_page_view: false });
  google.gtag('event', 'page_view', { ...page, page_path: path, send_to: PLAY_MEASUREMENT_ID });
  previousLocation = location; lastPath = pathname;
}
function safeReferrer(): string {
  try { return document.referrer ? new URL(document.referrer).origin : ''; } catch { return ''; }
}
